import { defineStore } from 'pinia';
import type { Tourflow } from '@aviata/platform-types';

import { usePassengersState } from '@entities/passengers-state';

import { useDirectionState } from './use-direction-state';
import { useDatepickerState } from './use-datepicker-state';

export const useSearchFormStore = defineStore('search-form-store', () => {
  const {
    townFrom,
    hasTownFrom,

    countryTo,
    hasCountryTo,

    townsTo,
    hasTownsTo,

    resetDirectionState,
  } = useDirectionState();

  const {
    passengers,
    childrenAge,

    hasPassengers,
    isMaxPassengersCount,
    isMaxChildrenCount,
    isMaxAdultCount,

    resetPassengersState,
    setAdults,
    setChildren,
    setChildrenWithAges,
    setPassengers,
  } = usePassengersState();

  const {
    depDate,
    arrDate,
    isFlexibleDates,

    nightsDifferenceCount,

    setDepDate,
    setArrDate,

    resetDatepickerState,
  } = useDatepickerState();

  function reset() {
    resetDirectionState();
    resetPassengersState();
    resetDatepickerState();
  }

  function onCountryToSelect(country?: Tourflow.CountryTo) {
    if (country?.id !== countryTo.value?.id) {
      countryTo.value = country;
      townsTo.value = [];
      resetDatepickerState();
    }
  }

  /**
   * @description Updates `townFrom` data and resets the destination data
   *
   * 1. We should reset `countryTo` and `townsTo`, because there could be
   *    no tours available to selected country or town from the new selected town
   *
   * 2. We should reset selected dates, because there could be no tours available
   *    in specified time period
   *
   * 3. We should reset data only when the new town is different from selected town
   */
  function onTownFromChange(town: Tourflow.TownFrom) {
    if (town.id !== townFrom.value?.id) {
      townFrom.value = town;
      countryTo.value = undefined;
      townsTo.value = null;
      resetDatepickerState();
    }
  }

  /**
   * @description Validates and updates `townsTo` data and resets the datepicker if needed
   *
   * Since the `calendar` API can accept town to IDs as a parameter and return dates for the specified towns,
   * we need to check if the IDs are truly changed and  then clear the datepicker state,
   * so we will minimize the empty search amount
   */
  function onTownsToChange(townsToList: Tourflow.TownTo[] | null) {
    if (townsToList === townsTo.value) {
      return;
    }

    /*
     * Simple check for cases where changes are obvious
     */
    if (
      !townsToList ||
      !townsTo.value ||
      townsToList.length !== townsTo.value.length
    ) {
      townsTo.value = townsToList;
      resetDatepickerState();
      return;
    }

    /*
     * Here we use `Set`, for cases, when the new towns-to count is the same as the old one
     * and check if new towns are different from existing ones
     */
    const townsToSet = new Set([...townsTo.value, ...townsToList]);

    if (townsToSet.size === townsTo.value?.length) {
      return;
    }

    townsTo.value = townsToList;
    resetDatepickerState();
  }

  return {
    townFrom,
    hasTownFrom,

    countryTo,
    hasCountryTo,

    townsTo,
    hasTownsTo,

    passengers,
    childrenAge,

    hasPassengers,
    isMaxPassengersCount,
    isMaxChildrenCount,
    isMaxAdultCount,
    isFlexibleDates,

    setAdults,
    setChildren,
    setChildrenWithAges,
    setPassengers,

    depDate,

    arrDate,

    nightsDifferenceCount,

    setDepDate,
    setArrDate,
    resetDatepickerState,

    onCountryToSelect,
    onTownFromChange,
    onTownsToChange,
    reset,
  };
});
